import React, { useMemo, useState } from 'react';
import { Input, Select, Space, Button } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import TableComponent from './TableComponent';

/**
 * Composant générique qui ajoute recherche globale + filtres par colonne
 * autour du TableComponent (rendu + export).
 *
 * Convention colonne :
 * - searchable: boolean -> incluse dans la recherche globale
 * - searchValue?: (row) => string -> valeur custom pour la recherche
 * - filterType: 'select' -> active un Select
 * - filterOptions: { label, value }[] -> options du filtre
 * - filterMultiple?: boolean -> mode multiple sur le Select
 * - filterMatcher?: (cellValue, filterValue) => boolean -> matching custom
 */
const ExportableFilterableTable = ({
    columns = [],
    dataSource = [],
    searchPlaceholder = 'Rechercher...',
    showInlineControls = true,
    toolbarExtras,
    defaultSearchValue = '',
    defaultFilters = {},
    onSearchChange,
    onFiltersChange,
    showExport = true,
    exportFileName,
    loading,
    pagination,
    ...tableProps
}) => {
    const [searchValue, setSearchValue] = useState(defaultSearchValue);
    const [columnFilters, setColumnFilters] = useState(defaultFilters);

    const searchableColumns = useMemo(
        () => columns.filter(col => col.searchable),
        [columns]
    );

    const filterableColumns = useMemo(
        () => columns.filter(col => col.filterType && col.filterOptions),
        [columns]
    );

    const normalize = (value) => {
        if (value === undefined || value === null) return '';
        if (typeof value === 'string') return value.toLowerCase();
        return String(value).toLowerCase();
    };

    const getSearchValue = (row, col) => {
        if (typeof col.searchValue === 'function') {
            return col.searchValue(row);
        }
        if (col.dataIndex) {
            return row[col.dataIndex];
        }
        return '';
    };

    const applyFilters = (row) => {
        // Recherche globale
        const term = normalize(searchValue.trim());
        if (term) {
            const match = searchableColumns.some(col => normalize(getSearchValue(row, col)).includes(term));
            if (!match) return false;
        }

        // Filtres par colonne
        for (const [dataIndex, value] of Object.entries(columnFilters)) {
            const isArrayFilter = Array.isArray(value);
            const hasValue = isArrayFilter ? value.length > 0 : value !== undefined && value !== null && value !== '';
            if (!hasValue) continue;

            const column = columns.find(col => col.dataIndex === dataIndex);
            const cellValue = column?.dataIndex ? row[column.dataIndex] : undefined;

            if (typeof column?.filterMatcher === 'function') {
                if (!column.filterMatcher(cellValue, value)) {
                    return false;
                }
                continue;
            }

            if (isArrayFilter) {
                if (!value.includes(cellValue)) {
                    return false;
                }
            } else if (cellValue !== value) {
                return false;
            }
        }

        return true;
    };

    const filteredData = useMemo(() => dataSource.filter(applyFilters), [dataSource, searchValue, columnFilters, columns]);

    const handleSearchChange = (e) => {
        const next = e.target.value;
        setSearchValue(next);
        if (onSearchChange) onSearchChange(next);
    };

    const handleFilterChange = (dataIndex, value) => {
        setColumnFilters((prev) => {
            const next = { ...prev, [dataIndex]: value };
            if (onFiltersChange) onFiltersChange(next);
            return next;
        });
    };

    const resetFilters = () => {
        setSearchValue('');
        setColumnFilters({});
        if (onSearchChange) onSearchChange('');
        if (onFiltersChange) onFiltersChange({});
    };

    const hasActiveFilters = useMemo(() => {
        if (searchValue) return true;
        return Object.values(columnFilters).some(val => {
            if (Array.isArray(val)) return val.length > 0;
            return val !== undefined && val !== null && val !== '';
        });
    }, [columnFilters, searchValue]);

    const toolbarContent = (
        <Space wrap>
            {showInlineControls && searchableColumns.length > 0 && (
                <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={handleSearchChange}
                    style={{ minWidth: 240 }}
                />
            )}

            {showInlineControls && filterableColumns.map(col => (
                <Select
                    key={col.dataIndex || col.key}
                    allowClear
                    mode={col.filterMultiple ? 'multiple' : undefined}
                    placeholder={col.filterPlaceholder || `Filtrer ${col.title || col.dataIndex}`}
                    options={col.filterOptions}
                    style={{ minWidth: 200 }}
                    value={columnFilters[col.dataIndex]}
                    onChange={(value) => handleFilterChange(col.dataIndex, value)}
                />
            ))}

            {showInlineControls && hasActiveFilters && (
                <Button icon={<ReloadOutlined />} onClick={resetFilters}>
                    Réinitialiser
                </Button>
            )}

            {toolbarExtras}
        </Space>
    );

    return (
        <TableComponent
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            pagination={pagination}
            toolbarContent={toolbarContent}
            showExport={showExport}
            exportFileName={exportFileName}
            {...tableProps}
        />
    );
};

export default ExportableFilterableTable;
